import React, { useCallback, useEffect, useState } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Receipt, FileCheck2, Download, Copy, XCircle, Loader2, MessageCircle, Landmark, Trash2, PlaneTakeoff, Undo2, FileMinus2, FilePlus2, TrendingUp, FileSpreadsheet, FileDown } from 'lucide-react';

const authHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const typeMeta = {
  quote: { label: 'Quote', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  invoice: { label: 'Tax Invoice', icon: FileCheck2, color: 'bg-emerald-100 text-emerald-800' },
  receipt: { label: 'Receipt', icon: Receipt, color: 'bg-amber-100 text-amber-800' },
  credit_note: { label: 'Credit Note', icon: FileMinus2, color: 'bg-rose-100 text-rose-800' },
  debit_note: { label: 'Debit Note', icon: FilePlus2, color: 'bg-purple-100 text-purple-800' },
};

const PAYMENT_MODES = ['Bank Transfer', 'UPI', 'Razorpay', 'Cash', 'Cheque'];
const ACCOUNTS = ['IDFC First Bank', 'Axis Bank', 'Razorpay', 'RazorpayX', 'Petty Cash', "Soumen's Personal", 'Undeposited Fund', 'Cash', 'UPI', 'Bank Remittance'];
const NOTE_TYPES = ['credit_note', 'debit_note'];

const DocumentDialog = ({ open, onClose, docType, tourId, queryId, onCreated }) => {
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([{ qty: 1 }]);
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const isNote = NOTE_TYPES.includes(docType);

  useEffect(() => {
    if (!open || docType === 'receipt') return;
    fetch(`${API}/docs/items`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [open, docType]);

  useEffect(() => {
    if (!open || !isNote) return;
    fetch(`${API}/docs?doc_type=invoice`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setInvoices((Array.isArray(d) ? d : []).filter((i) => i.status !== 'cancelled')))
      .catch(() => {});
  }, [open, isNote]);

  useEffect(() => {
    if (!open) return;
    setForm({});
    setLines([{ qty: 1 }]);
    if (isNote) return;
    const src = tourId ? `tour_id=${tourId}` : queryId ? `query_id=${queryId}` : '';
    if (!src) return;
    setLoading(true);
    fetch(`${API}/docs/prefill?${src}`, { credentials: 'include', headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        setForm({
          customer_name: d.customer?.name || '', customer_address: d.customer?.address || '',
          subject: d.subject || '', description: d.line?.description || '', rate: d.line?.rate || '',
          tour_id: d.tour_id || tourId || '',
          query_id: d.query_id || queryId || '', customer_id: d.customer_id || '',
          customer_phone: d.customer?.phone || '',
          payments_made: '', place_of_supply: '', create_payment_link: docType === 'quote', payment_link_amount: '',
        });
        if (d.lines?.length) setLines(d.lines.map((l) => ({ ...l })));
      })
      .finally(() => setLoading(false));
  }, [open, docType, tourId, queryId, isNote]);

  const pickInvoice = (number) => {
    const inv = invoices.find((i) => i.number === number);
    if (!inv) { setForm((f) => ({ ...f, against_number: number, against_doc_id: '' })); return; }
    setForm((f) => ({
      ...f,
      against_number: inv.number, against_doc_id: inv.id,
      customer_name: inv.customer?.name || '', customer_address: inv.customer?.address || '',
      customer_phone: inv.customer?.phone || '', place_of_supply: inv.place_of_supply || '',
      subject: f.subject || inv.subject || '',
      tour_id: inv.tour_id || '', customer_id: inv.customer_id || '',
    }));
    if (inv.lines?.length) setLines(inv.lines.map((l) => ({ ...l })));
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { qty: 1 }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));

  const saveNewItem = async (name) => {
    if (!name || items.some((it) => it.name.toLowerCase() === name.toLowerCase())) return;
    const res = await fetch(`${API}/docs/items`, {
      method: 'POST', credentials: 'include', headers: authHeaders(), body: JSON.stringify({ name }),
    });
    if (res.ok) { const it = await res.json(); setItems((p) => [...p, it].sort((a, b) => a.name.localeCompare(b.name))); toast.success(`"${name}" added to item list`); }
    else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        doc_type: docType,
        customer: { name: form.customer_name || '', address: form.customer_address || '', phone: form.customer_phone || '' },
        subject: form.subject || '',
        place_of_supply: form.place_of_supply || '',
        payments_made: parseFloat(form.payments_made) || 0,
        payment_mode: form.payment_mode || '',
        received_into: form.received_into || '',
        reference: form.reference || '',
        against_number: form.against_number || '',
        against_doc_id: form.against_doc_id || '',
        reason: form.reason || '',
        tour_id: form.tour_id || '', query_id: form.query_id || '', customer_id: form.customer_id || '',
        create_payment_link: !isNote && !!form.create_payment_link,
        payment_link_amount: parseFloat(form.payment_link_amount) || 0,
      };
      if (docType === 'receipt') {
        body.line = { title: '', description: form.description || '', qty: 1, rate: parseFloat(form.rate) || 0 };
      } else {
        body.lines = lines.map((l) => ({ title: l.title || '', description: l.description || '', qty: parseFloat(l.qty) || 1, rate: parseFloat(l.rate) || 0 }));
      }
      const res = await fetch(`${API}/docs`, {
        method: 'POST', credentials: 'include', headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`${data.number} created`);
      window.open(`${BACKEND}/api/public/docs/${data.id}.pdf`, '_blank');
      onCreated && onCreated(data);
      onClose();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const meta = typeMeta[docType] || typeMeta.quote;
  const subtotal = docType === 'receipt'
    ? (parseFloat(form.rate) || 0)
    : lines.reduce((s, l) => s + (parseFloat(l.qty) || 1) * (parseFloat(l.rate) || 0), 0);
  const totalWithGst = docType === 'receipt' ? subtotal : subtotal * 1.05;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="document-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><meta.icon className="w-4 h-4" />New {meta.label}</DialogTitle>
          <DialogDescription className="sr-only">Create a new {meta.label}</DialogDescription></DialogHeader>
        {loading ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <form onSubmit={submit} className="space-y-3 mt-2">
            {isNote && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
                <div><Label>Against invoice</Label>
                  <Input list="pd-invoice-options" data-testid="note-against-input" value={form.against_number || ''} onChange={(e) => pickInvoice(e.target.value)} placeholder="Pick an invoice (prefills customer & items)" />
                  <datalist id="pd-invoice-options">{invoices.map((i) => <option key={i.id} value={i.number}>{`${i.customer?.name || ''} · ₹${(i.total || 0).toLocaleString('en-IN')}`}</option>)}</datalist>
                </div>
                <div><Label>Reason *</Label>
                  <Input data-testid="note-reason-input" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={docType === 'credit_note' ? 'e.g. Tour cancelled — partial refund' : 'e.g. Extra night added post-invoice'} required /></div>
                <p className="text-[11px] text-muted-foreground">{docType === 'credit_note' ? 'A credit note reduces your GST liability in the month it is raised.' : 'A debit note adds to your GST liability in the month it is raised.'}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Customer *</Label><Input data-testid="doc-customer-input" value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required /></div>
              <div><Label>Place of supply (state)</Label><Input data-testid="doc-state-input" value={form.place_of_supply || ''} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} placeholder="e.g. Tamil Nadu" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Customer address</Label><Input value={form.customer_address || ''} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} /></div>
              <div><Label>Customer phone (WhatsApp)</Label><Input data-testid="doc-phone-input" value={form.customer_phone || ''} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="+91…" /></div>
            </div>
            <div><Label>Subject</Label><Input value={form.subject || ''} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
            {docType !== 'receipt' && (
              <div className="space-y-3">
                <Label className="font-semibold">Items</Label>
                {lines.map((ln, i) => {
                  const known = items.some((it) => it.name.toLowerCase() === (ln.title || '').toLowerCase());
                  return (
                    <div key={i} className="p-3 rounded-lg border bg-muted/20 space-y-2" data-testid={`doc-line-${i}`}>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1"><Label className="text-xs">Item</Label>
                          <Input list="pd-item-options" data-testid={`doc-line-title-${i}`} value={ln.title || ''} onChange={(e) => setLine(i, { title: e.target.value })} placeholder="Pick or type an item" />
                        </div>
                        {ln.title && !known && (
                          <Button type="button" size="sm" variant="outline" className="h-9 whitespace-nowrap" onClick={() => saveNewItem(ln.title)} data-testid={`doc-line-save-item-${i}`}>+ Save to list</Button>
                        )}
                        {lines.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0 text-destructive" onClick={() => removeLine(i)} data-testid={`doc-line-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                      <Textarea rows={i === 0 ? 4 : 2} placeholder="Description (itinerary, inclusions…)" value={ln.description || ''} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`doc-line-desc-${i}`} />
                      <div className="grid grid-cols-3 gap-2 items-end">
                        <div><Label className="text-xs">Qty</Label><Input type="number" step="1" min="1" value={ln.qty ?? ''} onChange={(e) => setLine(i, { qty: e.target.value })} /></div>
                        <div><Label className="text-xs">Rate (₹, pre-GST)</Label><Input data-testid={`doc-line-rate-${i}`} type="number" step="0.01" min="0" value={ln.rate ?? ''} onChange={(e) => setLine(i, { rate: e.target.value })} /></div>
                        <p className="text-sm text-muted-foreground pb-2 text-right">₹{(((parseFloat(ln.qty) || 1) * (parseFloat(ln.rate) || 0))).toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  );
                })}
                <datalist id="pd-item-options">{items.map((o) => <option key={o.id} value={o.name} />)}</datalist>
                <div className="flex items-center justify-between">
                  <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="doc-add-line-btn">+ Add item</Button>
                  <p className="text-sm font-medium">Sub ₹{subtotal.toLocaleString('en-IN')} · <span className="text-[13px] text-muted-foreground">+5% GST</span> = <span className="font-bold">₹{totalWithGst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></p>
                </div>
              </div>
            )}
            {docType === 'receipt' && (
              <>
                <div><Label>Received towards</Label>
                  <Textarea data-testid="doc-desc-input" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div><Label>Amount received (₹) *</Label>
                    <Input data-testid="doc-rate-input" type="number" step="0.01" min="0.01" value={form.rate || ''} onChange={(e) => setForm({ ...form, rate: e.target.value })} required /></div>
                  <p className="text-sm text-muted-foreground pb-2">₹{subtotal.toLocaleString('en-IN')}</p>
                </div>
              </>
            )}
            {docType === 'invoice' && (
              <div><Label>Payments already made (₹)</Label><Input type="number" step="0.01" value={form.payments_made || ''} onChange={(e) => setForm({ ...form, payments_made: e.target.value })} /></div>
            )}
            {docType === 'receipt' && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Payment mode</Label>
                    <Input list="pd-pay-modes" data-testid="receipt-mode-input" value={form.payment_mode || ''} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} placeholder="Bank Transfer / UPI / Razorpay" />
                    <datalist id="pd-pay-modes">{PAYMENT_MODES.map((o) => <option key={o} value={o} />)}</datalist>
                  </div>
                  <div><Label>Against (quote no.)</Label><Input value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="QT-000185" /></div>
                </div>
                <div><Label>Received into (account)</Label>
                  <Input list="pd-accounts" data-testid="received-into-input" value={form.received_into || ''} onChange={(e) => setForm({ ...form, received_into: e.target.value })} placeholder="IDFC First Bank / Razorpay / Cash…" />
                  <datalist id="pd-accounts">{ACCOUNTS.map((o) => <option key={o} value={o} />)}</datalist>
                </div>
              </>
            )}
            {docType !== 'receipt' && !isNote && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid="doc-paylink-check" checked={!!form.create_payment_link} onCheckedChange={(c) => setForm({ ...form, create_payment_link: !!c })} />
                  Embed a live Razorpay payment link
                </label>
                {form.create_payment_link && (
                  <div><Label>{docType === 'quote' ? 'Deposit amount (₹)' : 'Balance amount (₹)'}</Label>
                    <Input data-testid="doc-paylink-amount" type="number" step="0.01" value={form.payment_link_amount || ''} onChange={(e) => setForm({ ...form, payment_link_amount: e.target.value })}
                      placeholder={docType === 'quote' ? `50% deposit ≈ ₹${Math.round(totalWithGst / 2).toLocaleString('en-IN')}` : ''} /></div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="doc-create-btn">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Generate PDF</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Cancellation policy tiers (matches the T&C printed on quotes/invoices).
const policyTier = (days) => {
  if (days >= 30) return { pct: 75, label: '30+ days before arrival — 75% refund' };
  if (days >= 15) return { pct: 50, label: '15–30 days before arrival — 50% refund' };
  if (days >= 0) return { pct: 0, label: 'Less than 15 days before arrival — no refund per policy' };
  return { pct: 0, label: 'Trip already started / past — no refund per policy' };
};

const RefundDialog = ({ open, onClose, doc, onDone }) => {
  const [form, setForm] = useState({ method: 'manual' });
  const [payments, setPayments] = useState([]);
  const [loadingPays, setLoadingPays] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arrival, setArrival] = useState('');

  useEffect(() => {
    if (!open) return;
    setArrival('');
    if (!doc?.tour_id) return;
    fetch(`${API}/pollen/tours/${doc.tour_id}`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : {}))
      .then((t) => { if (t?.start_date) setArrival(t.start_date); })
      .catch(() => {});
  }, [open, doc]);

  useEffect(() => {
    if (!open) return;
    setForm({ method: 'manual', amount: '', reason: '', mode: '', account: '', reference: '', razorpay_payment_id: '' });
    setPayments([]);
    if (doc?.payment_link_id) {
      setLoadingPays(true);
      fetch(`${API}/docs/${doc.id}/razorpay-payments`, { credentials: 'include', headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : { payments: [] }))
        .then((d) => setPayments((d.payments || []).filter((p) => p.status === 'captured')))
        .catch(() => {})
        .finally(() => setLoadingPays(false));
    }
  }, [open, doc]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.method === 'razorpay' && !window.confirm(`This will REFUND ₹${form.amount} via Razorpay — real money moves immediately. Continue?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/docs/refunds`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({
          doc_id: doc?.id || '', amount: parseFloat(form.amount) || 0, method: form.method,
          mode: form.mode || '', account: form.account || '', reference: form.reference || '',
          reason: form.reason || '', razorpay_payment_id: form.razorpay_payment_id || '',
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Refund of ₹${(d.amount || 0).toLocaleString('en-IN')} recorded${form.method === 'razorpay' ? ' — Razorpay is processing it' : ''}`);
      onDone && onDone();
      onClose();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const paidBase = doc ? Math.max(((doc.payments_made > 0 ? doc.payments_made : doc.total) || 0) - (doc.refunded_total || 0), 0) : 0;
  const daysToArrival = arrival ? Math.ceil((new Date(`${arrival}T00:00:00`) - Date.now()) / 86400000) : null;
  const tier = daysToArrival === null ? null : policyTier(daysToArrival);
  const suggested = tier ? Math.round(paidBase * tier.pct) / 100 : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Undo2 className="w-4 h-4" />Issue Refund{doc ? ` — ${doc.number}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">Record a manual refund or process a live Razorpay refund</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-1">
          {doc && <p className="text-xs text-muted-foreground -mt-1">{doc.customer?.name} · total ₹{(doc.total || 0).toLocaleString('en-IN')}{doc.payments_made > 0 ? ` · paid ₹${doc.payments_made.toLocaleString('en-IN')}` : ''}{doc.refunded_total > 0 ? ` · already refunded ₹${doc.refunded_total.toLocaleString('en-IN')}` : ''}</p>}
          {doc && (
            <div className="p-2.5 rounded-lg border bg-emerald-50/60 border-emerald-200 space-y-2" data-testid="refund-policy-helper">
              <p className="text-[11px] font-semibold text-emerald-900">Cancellation policy helper <span className="font-normal text-emerald-700">(75% · 50% · 0% by days before arrival)</span></p>
              <div className="flex items-center gap-2">
                <div className="flex-1"><Label className="text-xs">Arrival / tour start date</Label>
                  <Input type="date" className="h-8" value={arrival} onChange={(e) => setArrival(e.target.value)} data-testid="refund-arrival-input" /></div>
                {tier && (
                  <div className="flex-1 text-right">
                    <p className="text-[11px] text-emerald-800">{daysToArrival >= 0 ? `${daysToArrival} day${daysToArrival === 1 ? '' : 's'} to arrival` : 'Past date'} · <b>{tier.pct}% tier</b></p>
                    <p className="text-sm font-bold text-emerald-900" data-testid="refund-suggested-amount">₹{suggested.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                  </div>
                )}
              </div>
              {tier ? (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] text-emerald-700">{tier.label} · base ₹{paidBase.toLocaleString('en-IN')} (paid − already refunded)</p>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-100 whitespace-nowrap" disabled={suggested <= 0}
                    onClick={() => setForm({ ...form, amount: String(suggested), reason: `Cancelled ${daysToArrival} day${daysToArrival === 1 ? '' : 's'} before arrival — ${tier.pct}% refund per policy` })}
                    data-testid="refund-apply-suggested-btn">Use ₹{suggested.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Button>
                </div>
              ) : (
                <p className="text-[10px] text-emerald-700">Pick the arrival date to get the policy-based suggestion. You can always type a custom amount below.</p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={form.method === 'manual' ? 'default' : 'outline'} className="flex-1" onClick={() => setForm({ ...form, method: 'manual' })} data-testid="refund-method-manual">Manual record</Button>
            <Button type="button" size="sm" variant={form.method === 'razorpay' ? 'default' : 'outline'} className="flex-1" onClick={() => setForm({ ...form, method: 'razorpay' })} disabled={!doc?.payment_link_id} data-testid="refund-method-razorpay">Via Razorpay</Button>
          </div>
          {form.method === 'razorpay' && (
            <div className="p-2.5 rounded-lg border bg-amber-50 border-amber-200 space-y-2">
              <p className="text-[11px] text-amber-800 font-medium">Live refund — money returns to the customer's original payment method.</p>
              {loadingPays ? <p className="text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading payments…</p>
                : payments.length === 0 ? <p className="text-xs text-muted-foreground">No captured Razorpay payments found on this document.</p>
                : (
                  <div className="space-y-1">
                    {payments.map((p) => (
                      <label key={p.payment_id} className="flex items-center gap-2 text-xs p-1.5 rounded border bg-white cursor-pointer" data-testid={`refund-payment-${p.payment_id}`}>
                        <input type="radio" name="rzp-pay" checked={form.razorpay_payment_id === p.payment_id} onChange={() => setForm({ ...form, razorpay_payment_id: p.payment_id, amount: form.amount || String(p.amount) })} />
                        <span className="font-mono">{p.payment_id}</span><span className="ml-auto font-semibold">₹{p.amount.toLocaleString('en-IN')}</span>{p.method && <span className="text-muted-foreground">{p.method}</span>}
                      </label>
                    ))}
                  </div>
                )}
            </div>
          )}
          <div><Label>Refund amount (₹) * <span className="text-[10px] text-muted-foreground font-normal">— editable, override with any custom amount</span></Label>
            <Input data-testid="refund-amount-input" type="number" step="0.01" min="0.01" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></div>
          <div><Label>Reason</Label>
            <Input data-testid="refund-reason-input" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Tour cancelled 20 days before arrival — 50% refund" /></div>
          {form.method === 'manual' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Mode</Label>
                  <Input list="pd-refund-modes" data-testid="refund-mode-input" value={form.mode || ''} onChange={(e) => setForm({ ...form, mode: e.target.value })} placeholder="Bank Transfer / UPI…" />
                  <datalist id="pd-refund-modes">{PAYMENT_MODES.map((o) => <option key={o} value={o} />)}</datalist>
                </div>
                <div><Label>Paid from (account)</Label>
                  <Input list="pd-refund-accounts" data-testid="refund-account-input" value={form.account || ''} onChange={(e) => setForm({ ...form, account: e.target.value })} placeholder="IDFC First Bank…" />
                  <datalist id="pd-refund-accounts">{ACCOUNTS.map((o) => <option key={o} value={o} />)}</datalist>
                </div>
              </div>
              <div><Label>Reference (UTR / txn id)</Label>
                <Input data-testid="refund-reference-input" value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || (form.method === 'razorpay' && !form.razorpay_payment_id)} data-testid="refund-submit-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{form.method === 'razorpay' ? 'Refund via Razorpay' : 'Record Refund'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentsPanel = ({ tourId = '', queryId = '', compact = false }) => {
  const [docs, setDocs] = useState([]);
  const [dialogType, setDialogType] = useState(null);
  const [refundDoc, setRefundDoc] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [gst, setGst] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const isGlobal = !tourId && !queryId;

  useEffect(() => {
    if (!isGlobal) return;
    fetch(`${API}/docs/gst-summary`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setGst(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API}/docs/revenue-summary`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRevenue(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [isGlobal]);

  const loadRefunds = useCallback(() => {
    const q = tourId ? `?tour_id=${tourId}` : '';
    fetch(`${API}/docs/refunds${q}`, { credentials: 'include', headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRefunds(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [tourId]);

  const load = useCallback(async () => {
    const q = tourId ? `?tour_id=${tourId}` : queryId ? `?query_id=${queryId}` : '';
    try {
      const r = await fetch(`${API}/docs${q}`, { credentials: 'include', headers: authHeaders() });
      if (r.ok) setDocs(await r.json());
    } catch (e) { console.error(e); }
  }, [tourId, queryId]);

  useEffect(() => { load(); loadRefunds(); }, [load, loadRefunds]);

  const brandedUrl = (d) => d.branded_url || (d.slug && d.kind_path ? `${window.location.origin}/${d.slug}/${d.kind_path}/${d.number}` : `${BACKEND}/api/public/docs/${d.id}.pdf`);
  const ogUrl = (d) => d.og_url || brandedUrl(d);

  const waShare = (d) => {
    const link = ogUrl(d);
    let text = `Hello${d.customer?.name ? ' ' + d.customer.name.split(' ')[0] : ''}! Here is your ${(typeMeta[d.doc_type]?.label || d.doc_type).toLowerCase()} ${d.number} from Pollen Dots${d.subject ? ' — ' + d.subject : ''}.\n\n📄 ${link}`;
    if (d.payment_link_url) text += `\n\n💳 Pay securely: ${d.payment_link_url}`;
    text += '\n\nTRAVEL · CONNECT · CARE — pollendots.com';
    const phone = (d.customer?.phone || '').replace(/[^0-9]/g, '');
    const target = phone ? `https://wa.me/${phone.length === 10 ? '91' + phone : phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(target, '_blank');
  };

  const convertToTour = async (d) => {
    if (!window.confirm(`Create a draft tour from ${d.number}?`)) return;
    const r = await fetch(`${API}/docs/${d.id}/convert-to-tour`, { method: 'POST', credentials: 'include', headers: authHeaders() });
    const res = await r.json().catch(() => ({}));
    if (r.ok) {
      toast.success(res.already_linked ? 'Already linked to a tour — opening it' : `Draft tour created: ${res.tour_name}`);
      window.open(`/pollen-dots/tour/${res.tour_id}`, '_blank');
      load();
    } else toast.error(res.detail || 'Failed');
  };

  const cancelDoc = async (d) => {
    if (!window.confirm(`Cancel ${d.number}? It stays in the series with a CANCELLED watermark; any unpaid payment link is cancelled too.`)) return;
    const r = await fetch(`${API}/docs/${d.id}/cancel`, { method: 'POST', credentials: 'include', headers: authHeaders() });
    if (r.ok) { toast.success(`${d.number} cancelled`); load(); } else toast.error('Failed');
  };

  const openRefund = (d) => { setRefundDoc(d); setRefundOpen(true); };

  const exportStatement = async (month, fmt) => {
    try {
      const r = await fetch(`${API}/docs/gst-statement/${month}?fmt=${fmt}`, { credentials: 'include', headers: authHeaders() });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gst-statement-${month}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="documents-panel">
      <div className="flex flex-wrap gap-2">
        <Button size={compact ? 'sm' : 'default'} onClick={() => setDialogType('quote')} data-testid="new-quote-btn"><FileText className="w-4 h-4 mr-1" />New Quote</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={() => setDialogType('receipt')} data-testid="new-receipt-btn"><Receipt className="w-4 h-4 mr-1" />Payment Receipt</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={() => setDialogType('invoice')} data-testid="new-invoice-btn"><FileCheck2 className="w-4 h-4 mr-1" />Tax Invoice</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setDialogType('credit_note')} data-testid="new-credit-note-btn"><FileMinus2 className="w-4 h-4 mr-1" />Credit Note</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" onClick={() => setDialogType('debit_note')} data-testid="new-debit-note-btn"><FilePlus2 className="w-4 h-4 mr-1" />Debit Note</Button>
        <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={() => { setRefundDoc(null); setRefundOpen(true); }} data-testid="new-refund-btn"><Undo2 className="w-4 h-4 mr-1" />Refund</Button>
      </div>
      <Card>
        <CardContent className="pt-4">
          {docs.length === 0 ? <p className="text-sm text-muted-foreground py-2">No documents yet. Invoices auto-generate (as an exact copy of the accepted quote) the day after a tour completes.</p> : (
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Number</TableHead><TableHead>Type</TableHead><TableHead>Customer</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {docs.map((d) => {
                  const m = typeMeta[d.doc_type] || typeMeta.quote;
                  const pdfUrl = `${BACKEND}/api/public/docs/${d.id}.pdf`;
                  const refundable = ['quote', 'invoice', 'receipt'].includes(d.doc_type) && d.status !== 'cancelled';
                  return (
                    <TableRow key={d.id} data-testid={`doc-row-${d.number}`}>
                      <TableCell className="font-medium">{d.number}{d.auto_generated && <span className="text-[10px] text-muted-foreground ml-1">(auto)</span>}{d.against_number && <span className="text-[10px] text-muted-foreground ml-1">vs {d.against_number}</span>}</TableCell>
                      <TableCell><Badge className={m.color}>{m.label}</Badge></TableCell>
                      <TableCell className="text-sm">{d.customer?.name || '-'}</TableCell>
                      <TableCell className="text-right font-medium">₹{d.total?.toLocaleString('en-IN')}{d.refunded_total > 0 && <span className="block text-[10px] text-rose-600">-₹{d.refunded_total.toLocaleString('en-IN')} refunded</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.date}</TableCell>
                      <TableCell>{d.status === 'cancelled' ? <Badge className="bg-red-100 text-red-700">cancelled</Badge> : d.status === 'accepted' ? <Badge className="bg-emerald-100 text-emerald-800">accepted ✓</Badge> : <Badge variant="outline">issued</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          {d.doc_type === 'quote' && d.status !== 'cancelled' && !d.tour_id && (
                            <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${d.status === 'accepted' ? 'text-[#B16E32]' : 'text-muted-foreground'}`} title="Convert to draft tour" onClick={() => convertToTour(d)} data-testid={`doc-to-tour-${d.number}`}><PlaneTakeoff className="w-3.5 h-3.5" /></Button>
                          )}
                          {refundable && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" title="Issue refund" onClick={() => openRefund(d)} data-testid={`doc-refund-${d.number}`}><Undo2 className="w-3.5 h-3.5" /></Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" title="Send on WhatsApp" onClick={() => waShare(d)} data-testid={`doc-wa-${d.number}`}><MessageCircle className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Open PDF" onClick={() => window.open(pdfUrl, '_blank')} data-testid={`doc-pdf-${d.number}`}><Download className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copy branded link" onClick={() => { navigator.clipboard.writeText(brandedUrl(d)); toast.success('Branded link copied'); }} data-testid={`doc-copy-${d.number}`}><Copy className="w-3.5 h-3.5" /></Button>
                          {d.status !== 'cancelled' && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" title="Cancel" onClick={() => cancelDoc(d)} data-testid={`doc-cancel-${d.number}`}><XCircle className="w-3.5 h-3.5" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>
      {refunds.length > 0 && (
        <Card data-testid="refunds-card">
          <CardContent className="pt-4">
            <p className="text-sm font-semibold mb-2 flex items-center gap-1"><Undo2 className="w-3.5 h-3.5" />Refunds Issued</p>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Against</TableHead><TableHead>Customer</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Reason</TableHead><TableHead>Reference</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {refunds.map((r) => (
                  <TableRow key={r.id} data-testid={`refund-row-${r.id}`}>
                    <TableCell className="text-sm">{r.date}</TableCell>
                    <TableCell className="text-sm font-medium">{r.doc_number || '-'}</TableCell>
                    <TableCell className="text-sm">{r.customer_name || '-'}</TableCell>
                    <TableCell className="text-right font-medium text-rose-700">₹{(r.amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell><Badge variant="outline" className={r.method === 'razorpay' ? 'border-blue-300 text-blue-700' : ''}>{r.method === 'razorpay' ? 'Razorpay' : r.mode || 'Manual'}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{r.reason || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{r.reference || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          </CardContent>
        </Card>
      )}
      {isGlobal && revenue.length > 0 && (
        <Card data-testid="revenue-card">
          <CardContent className="pt-4">
            <p className="text-sm font-semibold mb-1 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Revenue Recognition</p>
            <p className="text-[11px] text-muted-foreground mb-2">Booking revenue counts on the quote acceptance date · GST revenue counts on the invoice date (net of credit/debit notes).</p>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Bookings</TableHead>
                <TableHead className="text-right">Booking Revenue</TableHead>
                <TableHead className="text-right">Invoices</TableHead>
                <TableHead className="text-right">GST Revenue</TableHead>
                <TableHead className="text-right">GST Tax</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {revenue.map((m) => (
                  <TableRow key={m.month} data-testid={`revenue-row-${m.month}`}>
                    <TableCell className="font-medium">{m.month}</TableCell>
                    <TableCell className="text-right">{m.bookings}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">₹{m.booking_revenue.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">{m.invoices}</TableCell>
                    <TableCell className="text-right font-semibold">₹{m.invoiced_revenue.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right text-muted-foreground">₹{m.gst_tax.toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          </CardContent>
        </Card>
      )}
      {isGlobal && gst.length > 0 && (
        <Card data-testid="gst-summary-card">
          <CardContent className="pt-4">
            <p className="text-sm font-semibold mb-2 flex items-center gap-1"><Landmark className="w-3.5 h-3.5" />Monthly GST Statement (invoices − credit notes + debit notes)</p>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Month</TableHead><TableHead className="text-right">INV</TableHead>
                <TableHead className="text-right">CN</TableHead><TableHead className="text-right">DN</TableHead>
                <TableHead className="text-right">Taxable</TableHead><TableHead className="text-right">IGST</TableHead>
                <TableHead className="text-right">CGST</TableHead><TableHead className="text-right">SGST</TableHead>
                <TableHead className="text-right">GST Payable</TableHead><TableHead className="text-right">Export</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {gst.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell className="font-medium">{m.month}</TableCell>
                    <TableCell className="text-right">{m.invoices}</TableCell>
                    <TableCell className="text-right text-rose-600">{m.credit_notes || 0}</TableCell>
                    <TableCell className="text-right text-purple-600">{m.debit_notes || 0}</TableCell>
                    <TableCell className="text-right">₹{m.taxable.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">₹{m.igst.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">₹{m.cgst.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right">₹{m.sgst.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right font-bold text-maroon-700">₹{m.total_tax.toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="CSV" onClick={() => exportStatement(m.month, 'csv')} data-testid={`gst-export-${m.month}-csv`}><FileDown className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-700" title="Excel" onClick={() => exportStatement(m.month, 'xlsx')} data-testid={`gst-export-${m.month}-xlsx`}><FileSpreadsheet className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#581C2B]" title="PDF" onClick={() => exportStatement(m.month, 'pdf')} data-testid={`gst-export-${m.month}-pdf`}><FileText className="w-3.5 h-3.5" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          </CardContent>
        </Card>
      )}
      <DocumentDialog open={!!dialogType} onClose={() => setDialogType(null)} docType={dialogType || 'quote'}
        tourId={tourId} queryId={queryId} onCreated={() => { load(); }} />
      <RefundDialog open={refundOpen} onClose={() => setRefundOpen(false)} doc={refundDoc} onDone={() => { load(); loadRefunds(); }} />
    </div>
  );
};

export default DocumentsPanel;
